import { z } from 'zod';
import { addBillingCycle, isBillableItem, isGatewayItem } from './billing';
import { BillingCycleSchema, CurrencySchema, SkuSchema } from './schemas';
import { Sku, SubscriptionItemOrigin, type BillingCycle, type Currency } from './enums';
import {
  PartnerSaleItemSchema,
  SaleItemSchema,
  checkSaleComposition,
  checkSalePrices,
  type PricingFloors,
  type SaleCompositionViolation,
  type SaleItemInput,
  type SalePriceViolation,
} from './assistedSale';

/**
 * Pós-venda pelo CRM (#737, F5 da #679): as regras puras que o servidor aplica
 * e a tela do CRM antecipa. Mesmo princípio de `assistedSale.ts` — uma cópia
 * só, para a UI nunca oferecer o que o servidor recusaria.
 */

// ---------------------------------------------------------------------------
// Pix Automático
// ---------------------------------------------------------------------------

/**
 * Pix Automático (BR): o teto do mandato cobre o valor do ciclo + folga para
 * reajuste futuro — sem isto a renovação do anual falharia. 30 % de folga. (O
 * IOF não entra no teto: absorvemos via `amount_includes_iof: 'always'`.)
 *
 * Mora aqui porque são duas perguntas com a mesma régua: "que teto a venda
 * pede?" (`checkoutSessionCore`) e "o módulo novo ainda cabe no mandato?" (o
 * pós-venda). Duas cópias do 1,3 seriam duas respostas.
 */
export const PIX_MANDATE_BUFFER = 1.3;

/** Teto de mandato que um total de ciclo pede. */
export function pixMandateAmountFor(totalCents: number): number {
  return Math.round(totalCents * PIX_MANDATE_BUFFER);
}

/**
 * O total novo exige mandato novo? Regra do owner (9.24): **quando o total
 * novo ultrapassa o mandato ÷ 1,3** — ou seja, quando ele já não caberia
 * com a folga que a venda original reservou. Esperar o total estourar o teto
 * em si gastaria a folga que existe para o reajuste anual.
 */
export function needsNewPixMandate(args: {
  readonly newTotalCents: number;
  readonly mandateCents: number;
}): boolean {
  return pixMandateAmountFor(args.newTotalCents) > args.mandateCents;
}

// ---------------------------------------------------------------------------
// Prazos
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;

/** Aviso mínimo ao cliente antes de um aumento valer (9.17). */
export const PRICE_INCREASE_NOTICE_MS = 30 * DAY_MS;

/** Prazo para pagar a proporção avulsa antes de o módulo sair (9.24 → 9.7). */
export const PRORATION_PAYMENT_WINDOW_MS = 5 * DAY_MS;

/**
 * Primeira renovação que cai **pelo menos 30 dias** depois do aviso (decisão do
 * owner, 2026-09-25). Anda de ciclo em ciclo a partir do fim do período atual:
 * num mensal com renovação daqui a 10 dias, o aumento vale na renovação
 * seguinte; num anual, na próxima renovação anual.
 */
export function priceIncreaseEffectiveAt(args: {
  readonly currentPeriodEnd: number;
  readonly cycle: BillingCycle;
  readonly now: number;
}): number {
  const earliest = args.now + PRICE_INCREASE_NOTICE_MS;
  let at = args.currentPeriodEnd;
  // Teto de voltas: um `currentPeriodEnd` muito no passado (doc quebrado) não
  // pode virar laço infinito dentro de uma callable.
  for (let i = 0; i < 1_000 && at < earliest; i += 1) {
    at = addBillingCycle(at, args.cycle);
  }
  return at;
}

/**
 * Proporção do ciclo corrente para um valor, pelos dias que faltam — a conta
 * que a Stripe faz na proração do cartão, feita aqui porque no Pix a proração
 * dentro do mandato não existe (9.24). Arredonda para o centavo; fora do
 * período (fim já passou, ou período degenerado) é zero.
 */
export function proportionalCents(args: {
  readonly amountCents: number;
  readonly now: number;
  readonly periodStart: number;
  readonly periodEnd: number;
}): number {
  const { amountCents, now, periodStart, periodEnd } = args;
  const length = periodEnd - periodStart;
  if (length <= 0 || now >= periodEnd || amountCents <= 0) return 0;
  const remaining = Math.min(periodEnd - Math.max(now, periodStart), length);
  return Math.round((amountCents * remaining) / length);
}

// ---------------------------------------------------------------------------
// Composição vendida hoje
// ---------------------------------------------------------------------------

/** Forma mínima de item para as regras do pós-venda. */
export type PostSaleItemFields = {
  readonly sku: string;
  readonly amount_cents: number;
  readonly removed_at?: number | null | undefined;
  readonly pending_removal_at?: number | null | undefined;
  readonly external_item_id?: string | null | undefined;
  readonly external_price_id?: string | null | undefined;
  readonly origin?: SubscriptionItemOrigin | undefined;
};

/**
 * "Este item foi VENDIDO?" — do gateway (venda direta) ou do parceiro (#743,
 * sem gateway). O concedido (`GRANT`) nunca é venda.
 */
export function isSoldItem(it: PostSaleItemFields): boolean {
  return isGatewayItem(it) || it.origin === SubscriptionItemOrigin.PARTNER;
}

/**
 * O que o cliente **comprou e continua comprando**: itens do gateway ainda
 * cobrados e sem remoção agendada, com o BASE à frente. É a composição sobre
 * a qual o piso do pacote é calculado depois de cada mudança.
 *
 * Item concedido (`GRANT`) fica de fora: não foi vendido, e somá-lo ao pacote
 * seria usar uma concessão nossa para cumprir o piso de uma venda. Item de
 * parceiro (#743) entra: é venda, e o piso D12 vale para ela.
 */
export function saleCompositionOf(items: readonly PostSaleItemFields[]): SaleItemInput[] {
  const sold = items.filter(
    (it) =>
      isBillableItem(it) &&
      isSoldItem(it) &&
      (it.pending_removal_at === null || it.pending_removal_at === undefined),
  );
  const base = sold.filter((it) => it.sku === Sku.BASE);
  const rest = sold.filter((it) => it.sku !== Sku.BASE);
  return [...base, ...rest].map((it) => ({ sku: it.sku, amountCents: it.amount_cents }));
}

/**
 * Total do plano DEPOIS das mudanças já agendadas: cada item vendido pelo
 * preço que vai valer (o aumento agendado, se houver), sem os que vão sair.
 * É a base da régua do mandato — dois aumentos que cabem cada um sozinho
 * podem não caber juntos.
 */
export function futureTotalCents(
  items: readonly (PostSaleItemFields & { scheduled_price?: { amount_cents: number } | null | undefined })[],
): number {
  return items
    .filter(
      (it) =>
        isBillableItem(it) &&
        isSoldItem(it) &&
        (it.pending_removal_at === null || it.pending_removal_at === undefined),
    )
    .reduce((sum, it) => sum + (it.scheduled_price?.amount_cents ?? it.amount_cents), 0);
}

export type PostSaleViolation = SaleCompositionViolation | SalePriceViolation;

/**
 * Piso e forma de uma mudança de pós-venda. Diferente da venda, **só os itens
 * tocados** passam pelo piso de item: o piso pode ter subido depois da venda, e
 * cobrar isso de um item que o consultor não mexeu travaria qualquer mudança
 * no cliente por causa de um preço que já está congelado (princípio 3). O piso
 * do **pacote** vale sempre, sobre a composição resultante.
 */
export function checkPostSaleChange(
  floors: PricingFloors,
  change: {
    readonly currency: Currency;
    readonly cycle: BillingCycle;
    /** Composição DEPOIS da mudança. */
    readonly composition: readonly SaleItemInput[];
    /** SKUs cujo preço a mudança define (item novo, reajustado, ciclo novo). */
    readonly changedSkus: readonly string[];
  },
): PostSaleViolation[] {
  const shape = checkSaleComposition(change.composition);
  if (shape.length > 0) return shape;
  const touched = new Set(change.changedSkus);
  const sale = { currency: change.currency, cycle: change.cycle, items: change.composition };
  return checkSalePrices(floors, sale).filter((v) =>
    v.kind === 'NO_FLOOR' || v.kind === 'BELOW_FLOOR' ? touched.has(v.sku) : true,
  );
}

// ---------------------------------------------------------------------------
// Contrato das callables (`apps/functions/src/billing/postSale/*`)
// ---------------------------------------------------------------------------

/** O lead ganho pela venda — é ele que autoriza o colaborador (mesmo gate da F2). */
export const PostSaleLeadRefSchema = z.object({
  boardId: z.string().min(1).max(128),
  leadId: z.string().min(1).max(128),
});
export type PostSaleLeadRef = z.infer<typeof PostSaleLeadRefSchema>;

export const GetSaleSubscriptionInput = PostSaleLeadRefSchema;
export type GetSaleSubscriptionInput = z.infer<typeof GetSaleSubscriptionInput>;

export const AddSaleItemInput = PostSaleLeadRefSchema.extend({
  sku: SkuSchema,
  amountCents: z.number().int().nonnegative(),
});
export type AddSaleItemInput = z.infer<typeof AddSaleItemInput>;

export const SaleItemSkuInput = PostSaleLeadRefSchema.extend({ sku: SkuSchema });
export type SaleItemSkuInput = z.infer<typeof SaleItemSkuInput>;

/**
 * Gera de novo o link que está com o cliente (#737): a Checkout Session vive
 * 24 h no máximo, e o prazo da proporção é maior. Com `sku`: o link da
 * proporção daquele módulo. Sem: o link de autorização do mandato pendente.
 */
export const RenewSaleLinkInput = PostSaleLeadRefSchema.extend({ sku: SkuSchema.optional() });
export type RenewSaleLinkInput = z.infer<typeof RenewSaleLinkInput>;

export const ChangeSaleItemPriceInput = PostSaleLeadRefSchema.extend({
  sku: SkuSchema,
  amountCents: z.number().int().nonnegative(),
});
export type ChangeSaleItemPriceInput = z.infer<typeof ChangeSaleItemPriceInput>;

export const ChangeSaleCycleInput = PostSaleLeadRefSchema.extend({
  billingCycle: BillingCycleSchema,
  /** Preço de CADA item vendido no ciclo novo — piso é por ciclo (D12). */
  items: z.array(SaleItemSchema).min(1).max(40),
});
export type ChangeSaleCycleInput = z.infer<typeof ChangeSaleCycleInput>;

export const CancelSaleSubscriptionInput = PostSaleLeadRefSchema.extend({
  // 120 = limite de `Subscription.cancelled_reason`: um motivo maior gravado no
  // doc reprovaria o `SubscriptionSchema` e deixaria a assinatura ilegível.
  reason: z.string().trim().min(3).max(120),
});
export type CancelSaleSubscriptionInput = z.infer<typeof CancelSaleSubscriptionInput>;

/**
 * Meio que paga as renovações da assinatura, como o pós-venda o vê.
 *
 * `boleto` entrou na A6 (#755, 2026-09-28 10:11 +0200): no Asaas o boleto recorrente é meio de
 * assinatura (no Stripe o pós-venda nunca o viu). Com ele o pós-venda completo
 * vale; com `pix` do Asaas, mudança de valor é recusada (`asaas_pix_value_change`
 * — a autorização do Pix Automático tem valor fixo, #755).
 */
export const PaymentRailSchema = z.enum(['card', 'pix', 'boleto', 'unknown']);
export type PaymentRail = z.infer<typeof PaymentRailSchema>;

/**
 * A `origin` de um item NA VIEW do CRM — não é `SubscriptionItemOrigin`.
 *
 * Origem: #761 — 2026-09-27 18:59 +0200 (auditoria da fase 1; a view é da
 * #737).
 *
 * Por quê: a view projeta `isGatewayItem(it) ? GATEWAY : GRANT`
 * (`getSaleSubscription.ts`), então aqui `GRANT` quer dizer "não passa pelo
 * gateway" e INCLUI o item de parceiro (`SubscriptionItemOrigin.PARTNER`, sem
 * ids de gateway). Usar `SubscriptionItemOrigin.GRANT` na tela sugeriria que a
 * view carrega a origem real do item.
 *
 * Usado por: `getSaleSubscription` (Functions, que monta), `postSaleDraft.ts`
 * e `SaleSubscriptionPage.tsx` (CRM `apps/crm`, que leem).
 *
 * Issues: #761, #737 (pós-venda).
 */
export const SaleSubscriptionItemViewOriginSchema = z.enum(['GATEWAY', 'GRANT']);
export const SaleSubscriptionItemViewOrigin = SaleSubscriptionItemViewOriginSchema.enum;
export type SaleSubscriptionItemViewOrigin = z.infer<typeof SaleSubscriptionItemViewOriginSchema>;

/** Um item como o CRM o vê. */
export const SaleSubscriptionItemViewSchema = z.object({
  sku: SkuSchema,
  amountCents: z.number().int().nonnegative(),
  origin: SaleSubscriptionItemViewOriginSchema,
  addedAt: z.number().int().nonnegative(),
  pendingRemovalAt: z.number().int().nonnegative().nullable(),
  /** O passe 5 já tirou o item da Stripe: a remoção não se desfaz mais. */
  removalInProgress: z.boolean(),
  pendingProration: z
    .object({
      amountCents: z.number().int().positive(),
      expiresAt: z.number().int().nonnegative(),
      url: z.url().nullable(),
    })
    .nullable(),
  scheduledPrice: z
    .object({
      amountCents: z.number().int().nonnegative(),
      effectiveAt: z.number().int().nonnegative(),
    })
    .nullable(),
});
export type SaleSubscriptionItemView = z.infer<typeof SaleSubscriptionItemViewSchema>;

export const GetSaleSubscriptionOutput = z.object({
  tenantId: z.string().min(1),
  status: z.string().min(1),
  billingCycle: BillingCycleSchema,
  currency: CurrencySchema,
  totalCents: z.number().int().nonnegative(),
  currentPeriodEnd: z.number().int().nonnegative(),
  paymentRail: PaymentRailSchema,
  pixMandateCents: z.number().int().positive().nullable(),
  /**
   * Gateway da assinatura (#755, A6). Opcional: servidor anterior à A6 não
   * manda, e aí é o Stripe.
   */
  provider: z.enum(['stripe', 'asaas']).optional(),
  /**
   * Anual parcelado do Asaas: número de parcelas (2–12) do ano vigente; `null`
   * fora dele (#755, A6). No parcelado, remoção, preço e ciclo valem na
   * renovação — a tela diz isso.
   */
  annualInstallments: z.number().int().min(2).max(12).nullable().optional(),
  /** Cancelamento agendado para o fim do período. */
  cancelAtPeriodEnd: z.boolean(),
  /**
   * #965 (2026-10-13 21:00 +0200, auditoria ALTO-2) — como o servidor confere
   * o preço do pós-venda deste cliente: `FLOOR` (preço negociado, ≥ piso) ou
   * `PARTNER_LICENSE` (cliente cujo pagador é o parceiro RESELLER,
   * `Subscription.bill_to`: preço = licença vigente `partner_license`, só
   * mensal — a tela trava preço e esconde a troca de ciclo). `.default`:
   * servidor anterior à #965 não manda, e aí é `FLOOR`.
   */
  pricingRule: z.enum(['FLOOR', 'PARTNER_LICENSE']).default('FLOOR'),
  pendingCycleChange: z
    .object({
      billingCycle: BillingCycleSchema,
      effectiveAt: z.number().int().nonnegative().nullable(),
      items: z.array(SaleItemSchema),
    })
    .nullable(),
  pendingMandateSetup: z
    .object({
      reason: z.enum(['CYCLE_CHANGE', 'PRICE_INCREASE', 'ADDON']),
      expiresAt: z.number().int().nonnegative(),
      url: z.url().nullable(),
    })
    .nullable(),
  items: z.array(SaleSubscriptionItemViewSchema),
});
export type GetSaleSubscriptionOutput = z.infer<typeof GetSaleSubscriptionOutput>;

/**
 * Retorno das ações que podem exigir o cliente: o link avulso da proporção
 * (módulo novo em Pix) ou o link de mandato novo (troca de ciclo ou aumento
 * acima do teto em Pix). `null` quando nada é pedido ao cliente.
 */
export const PostSaleActionOutput = z.object({
  ok: z.literal(true),
  customerLink: z
    .object({
      kind: z.enum(['PRORATION', 'MANDATE_SETUP']),
      url: z.url(),
      amountCents: z.number().int().nonnegative(),
      expiresAt: z.number().int().nonnegative(),
    })
    .nullable(),
  /** Quando a mudança vale (aumento agendado, remoção, ciclo, cancelamento). */
  effectiveAt: z.number().int().nonnegative().nullable(),
});
export type PostSaleActionOutput = z.infer<typeof PostSaleActionOutput>;

// ---------------------------------------------------------------------------
// #743 (F8 da #679) — pós-venda do PARCEIRO, sem gateway
// ---------------------------------------------------------------------------

/** A composição nova inteira: o que o parceiro cobra e repassa, item a item. */
export const PartnerUpdateSaleInput = PostSaleLeadRefSchema.extend({
  billingCycle: BillingCycleSchema,
  items: z
    .array(PartnerSaleItemSchema)
    .min(1)
    .max(30)
    .refine((items) => items.reduce((sum, it) => sum + it.remitCents, 0) > 0, {
      message: 'o repasse do pacote tem de ser maior que zero',
    }),
});
export type PartnerUpdateSaleInput = z.infer<typeof PartnerUpdateSaleInput>;

/** Encerrar o cliente do parceiro: o tenant é suspenso e a assinatura, cancelada. */
export const PartnerEndSaleInput = PostSaleLeadRefSchema.extend({
  reason: z.string().trim().min(3).max(120),
});
export type PartnerEndSaleInput = z.infer<typeof PartnerEndSaleInput>;

export const PartnerSaleSubscriptionViewSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string(),
  status: z.string(),
  billingCycle: BillingCycleSchema,
  currency: z.string(),
  /** Itens que seguem no próximo ciclo — a composição editável. */
  items: z.array(z.object({ sku: SkuSchema, amountCents: z.number().int(), remitCents: z.number().int() })),
  /**
   * Módulos retirados que ficam até onde o cliente pagou (#755): ativos até
   * `until` (epoch ms), sem reembolso. Fora de `items` para a tela não os
   * reenviar como "querido" — o que desfaria a saída.
   */
  leaving: z.array(z.object({ sku: SkuSchema, until: z.number().int() })).default([]),
  totalAmountCents: z.number().int(),
  totalRemitCents: z.number().int(),
  endedAt: z.number().int().nullable(),
});
export type PartnerSaleSubscriptionView = z.infer<typeof PartnerSaleSubscriptionViewSchema>;
